// src/services/forward.js
//
// 🟢 FORWARDING — every message type, byte-compatible with the phone
//    (ChatModal.handleForward, ~L4037-5450).
//
// THE ONE RULE THAT MATTERS: a forward goes to a DIFFERENT chat with DIFFERENT
// participants, so nothing sealed may be copied. The ciphertext in R2 is reused
// (same bytes, same object, no re-upload), but its symmetric key is unsealed with
// OUR key and RE-SEALED to the destination's devices. Copy `imagePerDeviceKeys`
// across verbatim and the recipient holds a box addressed to someone else's device:
// the photo downloads, decryption returns null, and the bubble dies as "unavailable".
//
// Marker fields the app expects (ChatModal:4296-4343):
//   forwarded: true            — BOOLEAN in Firestore, the STRING 'true' on the socket
//   originalSenderId: string   — who wrote it originally
//   originalSenderName: string — never empty; 'Unknown User' is the app's floor
//   forwardedFrom: string      — the chat/peer it came FROM (optional)
//   clientTempId: `temp-forward-…`
//
// 🟥 `forwarded` MUST NOT BE DOWNGRADED ON MERGE. The app keeps a sticky guard
//    (ChatModal:14194) because its own FCM bridge sometimes delivers a copy with the
//    flag missing. Anything on this side that merges message states must do the same.

import { sealMediaMeta, withSealedMeta } from '../crypto/mediaMeta';   // 🔐 caption + thumbnails under the media's key
import {
  collection, doc, getDoc, setDoc, serverTimestamp, arrayUnion,
} from 'firebase/firestore';
import { db } from '../config/firebase';
import { sealForUsers, unsealMediaKey, getDeviceKeys } from '../crypto/e2ee';
import { relayNewMessage } from './socket';
import { writeChatDoc } from './mediaUpload';
import { sealMembers } from './groupMembers';
import { setMessageDoc } from './sendDoc'; // 🖥📱 origin + my other devices' copy
import { albumVisibleIndices, narrowAlbum } from './mediaRuns'; // 🧩 one item of an album

/** The phone's per-type preview, sealed as the forwarded doc's `text`. */
export function forwardPreviewOf(m) {
  switch (m?.type) {
    case 'image': return '\u{1F4F7} Photo';
    case 'video': return '\u{1F3A5} Video';
    case 'audio': return '\u{1F3A4} Voice message';
    case 'document': return `\u{1F4C4} Document: ${m.documentName || 'File'}`;
    case 'location': return '\u{1F4CD} Location';
    case 'contact': return `\u{1F464} Contact: ${m._payload?.name || m.contactName || 'Unknown'}`;
    case 'flight': return `✈️ Flight: ${m.flightData?.flightNumber || m.flightNumber || 'Info'}`;
    case 'poll': return `\u{1F4CA} Poll: ${m._payload?.question || 'Poll'}`;
    // 🟥 'images' deliberately has NO branch — the phone leaves an album's forward text
    //    EMPTY and skips the seal entirely. It used to seal the empty string, and since
    //    NaCl happily decrypts an empty box back to '' while every reader tested
    //    `if (!plain)`, a success read as a failure. Match the fix, not the bug.
    case 'images': return '';
    case 'sticker': return '\u{1F49F} Sticker';
    case 'gif': return '\u{1F39E}️ GIF';
    default: return (m?._plain || m?.text || '').trim();
  }
}

/** Chat-list text for the destination chat. */
function chatDocPreview(m) {
  if (m?.type === 'text') return '\u{1F512} [Encrypted Message]';
  return forwardPreviewOf(m) || 'Forwarded a message';
}

/** Types that can never be forwarded. */
export function canForward(m) {
  if (!m) return false;
  if (m.isDeleted || m.deleted) return false;
  if (m.mediaDeletedFromStorage) return false;
  if (m.type === 'system' || m.systemType || m.type === 'missed_call') return false;
  if (m._pending || m._failed) return false;
  return true;
}

/**
 * Recover a media message's symmetric key.
 * Two shapes reach the web and both have to work:
 *   • a live Firestore doc — the key is sealed under `<type>PerDeviceKeys`
 *   • a phone-synced history row — the phone already unsealed it and shipped a
 *     plaintext `mediaKey` / `mediaKeys` in the export envelope
 */
async function symKeyOf(m, field) {
  // the phone's export ships the key already opened, under a flat `mediaKey`
  if (m.mediaKey?.k && m.mediaKey?.n) {
    return { k: m.mediaKey.k, n: m.mediaKey.n, ...(m.mediaKey.type ? { type: m.mediaKey.type } : {}) };
  }
  const sealedMap = m[field];
  if (!sealedMap) return { missing: true };   // legacy/unencrypted — nothing to re-seal
  const kn = await unsealMediaKey(sealedMap, m.senderId, m.senderDeviceId);
  if (!kn?.k || !kn?.n) return null;          // sealed, but NOT to us → caller must refuse
  return { k: kn.k, n: kn.n, ...(kn.type ? { type: kn.type } : {}) };
}

/** 🟥 WebHistorySync flattens EVERY non-album medium to a single `url` field — there is
 *  no videoUrl/audioUrl/documentUrl on a synced row. Reading only the live-doc field
 *  meant a voice note that was playing on screen refused to forward. */
const srcUrl = (m, ...fields) => {
  for (const f of fields) if (typeof m[f] === 'string' && m[f]) return m[f];
  return typeof m.url === 'string' && m.url ? m.url : null;
};

/**
 * 🟥 FAIL CLOSED. `unsealMediaKey` returns null for two ordinary reasons — the message
 *    predates this browser being paired, or the sender rotated their device key — and
 *    the first draft treated that as "no key needed" and forwarded the R2 ciphertext
 *    URL with no key map at all. The recipient then downloads a real object, decrypts
 *    nothing, and holds a permanently dead bubble that no retry can fix. A message
 *    whose key we cannot open is a message we cannot forward.
 */
function requireKey(kn, what) {
  if (kn === null) fail(`You can't forward that ${what} — it was encrypted before this browser was linked.`);
  return kn;
}

async function batchKeysOf(m) {
  if (Array.isArray(m.mediaKeys) && m.mediaKeys.length) return m.mediaKeys;
  if (!m.batchPerDeviceKeys) return null;
  const arr = await unsealMediaKey(m.batchPerDeviceKeys, m.senderId, m.senderDeviceId);
  return Array.isArray(arr) ? arr : null;
}

/**
 * 🟥 "SEALED SUCCESSFULLY" IS NOT THE SAME AS "THEY CAN READ IT". sealForUsers always
 *    includes OUR OWN devices, so its map is non-empty even when the recipient has no
 *    registered device at all — and a forward would report success while delivering
 *    something only we can open. This checks the destination has a key BEFORE anything
 *    is written, so the failure is an honest message instead of a silent black hole.
 */
async function assertReachable(participants, myUid) {
  const others = (participants || []).filter((u) => u && u !== myUid);
  if (!others.length) return;                 // Note to Self — our own devices are the point
  const found = await Promise.all(others.map((u) => getDeviceKeys(u).catch(() => ({}))));
  if (!found.some((k) => k && Object.keys(k).length)) {
    fail("That chat has no device set up for encryption yet — nothing was sent.");
  }
}

/** Seal a JSON payload to the destination. Returns null when there's nobody to seal to. */
async function sealTo(payload, participants) {
  const sealed = await sealForUsers(payload, participants || []);
  if (!sealed || !Object.keys(sealed.perDeviceKeys || {}).length) return null;
  return { map: sealed.perDeviceKeys, senderDeviceId: sealed.senderDeviceId };
}

class ForwardError extends Error {}
const fail = (msg) => { throw new ForwardError(msg); };

/**
 * Build the type-specific half of a forwarded message: the Firestore fields and the
 * matching socket `extra`. Everything sealed in here is sealed to `participants`,
 * the DESTINATION's devices — never carried over from the source.
 */
async function bodyFor(m, participants) {
  const F = {};   // Firestore doc fields
  const S = {};   // socket payload fields
  const put = (k, v) => { if (v !== undefined && v !== null) { F[k] = v; S[k] = v; } };

  switch (m.type) {
    case 'text':
      return { F, S };

    case 'image':
    case 'video':
    case 'sticker':
    case 'gif': {
      const isVideo = m.type === 'video';
      const url = isVideo
        ? srcUrl(m, 'videoUrl')
        : srcUrl(m, 'imageUrl', 'stickerUrl', 'gifUrl');
      if (!url) fail("That photo isn't available to forward yet.");
      const keyField = isVideo ? 'videoPerDeviceKeys' : 'imagePerDeviceKeys';
      const kn = requireKey(await symKeyOf(m, keyField), isVideo ? 'video' : 'photo');
      if (!kn.missing) {
        // ⚡ the bytes never move: same object in R2, a new wrapper on its key
        const seal = await sealTo(JSON.stringify({ k: kn.k, n: kn.n }), participants);
        if (!seal) fail("Couldn't secure that for the new chat — nothing was sent.");
        put(keyField, seal.map);
        put('senderDeviceId', seal.senderDeviceId);
      }
      put(isVideo ? 'videoUrl' : 'imageUrl', url);
      put(isVideo ? 'videoThumbnailBase64' : 'imageThumbnailBase64',
        isVideo ? m.videoThumbnailBase64 : m.imageThumbnailBase64);
      put('width', m.width); put('height', m.height);
      put(isVideo ? 'videoQuality' : 'imageQuality', isVideo ? m.videoQuality : m.imageQuality);
      if (isVideo && m.duration) put('duration', m.duration);
      // a sticker and a GIF are both type:'image' plus a flag; losing the flag makes
      // the renderer fall back to the ordinary photo branch
      if (m.isSticker || m.type === 'sticker') put('isSticker', true);
      if (m.isGif || m.type === 'gif') put('isGif', true);
      if (m.filterColor) put('filterColor', m.filterColor);
      if (m._caption || m.caption) { put('caption', m._caption || m.caption); F.originalCaption = m._caption || m.caption; }
      /* 🔐 caption + thumbnail under the photo's own key (../crypto/mediaMeta); a sticker has no key */
      const _meta = kn.missing ? null : sealMediaMeta(kn.k, { c: m._caption || m.caption, [isVideo ? 'v' : 't']: isVideo ? m.videoThumbnailBase64 : m.imageThumbnailBase64 });
      return { F: withSealedMeta(F, _meta), S: withSealedMeta(S, _meta) };
    }

    case 'images': {
      /* 🧩 a narrowed album (one item, or what is still part of it) carries the slots it
         holds; the key array is positional, so it is narrowed the same way */
      const urls = (m.images || []).filter((u) => typeof u === 'string' && /^https?:/.test(u));
      if (!urls.length || urls.length !== (m.images || []).length) {
        fail("That album isn't fully uploaded yet.");
      }
      const keysAll = await batchKeysOf(m);
      const keys = (Array.isArray(keysAll) && Array.isArray(m._sourceIndices)) ? m._sourceIndices.map((i) => keysAll[i]) : keysAll;
      // 🟥 the key array is POSITIONAL — keys[i] opens images[i]. A length mismatch is
      //    not a partial success, it's a silently scrambled album, so refuse instead.
      if (!keys || keys.length !== urls.length) fail("Couldn't read that album's keys.");
      const seal = await sealTo(JSON.stringify(keys), participants);
      if (!seal) fail("Couldn't secure that album for the new chat — nothing was sent.");
      put('images', urls);
      put('batchPerDeviceKeys', seal.map);
      put('senderDeviceId', seal.senderDeviceId);
      if (Array.isArray(m.batchThumbnails)) {
        F.batchThumbnails = m.batchThumbnails;
        S.batchThumbnails = m.batchThumbnails.slice(0, 4);   // FCM payload size cap
      }
      if (Array.isArray(m.mediaQualities)) put('mediaQualities', m.mediaQualities);
      if (m._caption || m.caption) put('caption', m._caption || m.caption);
      /* 🔐 caption + thumbnails under the album's own key; the relay, the first four */
      const _k0 = keys[0] && keys[0].k;
      const _cap = m._caption || m.caption;
      const _bt = Array.isArray(m.batchThumbnails) ? m.batchThumbnails : undefined;
      return {
        F: withSealedMeta(F, sealMediaMeta(_k0, { c: _cap, b: _bt })),
        S: withSealedMeta(S, sealMediaMeta(_k0, { c: _cap, b: _bt ? _bt.slice(0, 4) : undefined })),
      };
    }

    case 'audio': {
      const aUrl = srcUrl(m, 'audioUrl');
      if (!aUrl) fail("That voice message isn't available to forward.");
      const kn = requireKey(await symKeyOf(m, 'audioPerDeviceKeys'), 'voice message');
      if (!kn.missing) {
        const seal = await sealTo(JSON.stringify({ k: kn.k, n: kn.n }), participants);
        if (!seal) fail("Couldn't secure that recording for the new chat.");
        put('audioPerDeviceKeys', seal.map);
        put('senderDeviceId', seal.senderDeviceId);
      }
      put('audioUrl', aUrl);
      put('duration', m.duration);
      if (Array.isArray(m.waveform)) { F.waveform = m.waveform; S.waveform = JSON.stringify(m.waveform); }
      return { F, S };
    }

    case 'document': {
      const dUrl = srcUrl(m, 'documentUrl');
      if (!dUrl) fail("That document isn't available to forward.");
      const kn = requireKey(await symKeyOf(m, 'documentPerDeviceKeys'), 'document');
      if (!kn.missing) {
        const seal = await sealTo(JSON.stringify({ k: kn.k, n: kn.n }), participants);
        if (!seal) fail("Couldn't secure that document for the new chat.");
        put('documentPerDeviceKeys', seal.map);
        put('senderDeviceId', seal.senderDeviceId);
      }
      put('documentUrl', dUrl);
      put('documentName', m.documentName);
      put('documentSize', Number(m.documentSize) || 0);
      put('documentType', m.documentType || '');
      // 🟢 THE THUMBNAIL CAN BE CARRIED, and the phone can't do this. Its forward path
      //    re-RENDERS the first page from the decrypted file, which the instant path
      //    doesn't have — so an app-forwarded PDF arrives with no preview at all. Here
      //    the thumbnail is just another encrypted object with its own key, so
      //    unsealing and re-sealing it keeps the preview across the forward.
      //    Note the id lives in its OWN field, not the shared senderDeviceId.
      if (m.documentThumbUrl && m.documentThumbPerDeviceKeys) {
        try {
          const tk = await unsealMediaKey(
            m.documentThumbPerDeviceKeys, m.senderId,
            m.documentThumbSenderDeviceId || m.senderDeviceId,
          );
          if (tk?.k && tk?.n) {
            const tSeal = await sealTo(JSON.stringify({ k: tk.k, n: tk.n }), participants);
            if (tSeal) {
              put('documentThumbUrl', m.documentThumbUrl);
              put('documentThumbPerDeviceKeys', tSeal.map);
              put('documentThumbSenderDeviceId', tSeal.senderDeviceId);
              put('documentThumbWidth', m.documentThumbWidth || 0);
              put('documentThumbHeight', m.documentThumbHeight || 0);
            }
          }
        } catch { /* an icon card is still a perfectly good document bubble */ }
      }
      return { F, S };
    }

    case 'location': {
      const p = m._payload || {};
      const lat = Number(p.lat ?? p.latitude ?? m.latitude);
      const lng = Number(p.lng ?? p.longitude ?? m.longitude);
      if (!Number.isFinite(lat) || !Number.isFinite(lng)) fail("Couldn't read that location.");
      // ⛔ FAIL CLOSED — the coordinates live ONLY inside the seal, never on the doc
      const seal = await sealTo(JSON.stringify({ lat, lng, ...(p.name ? { name: p.name } : {}) }), participants);
      if (!seal) fail("Couldn't secure that location for the new chat.");
      put('locationPerDeviceKeys', seal.map);
      put('senderDeviceId', seal.senderDeviceId);
      put('live', false);          // a forwarded live location is a still one
      F.liveUntil = null; F.liveLocationId = null;
      return { F, S };
    }

    case 'contact': {
      const p = m._payload || {};
      const seal = await sealTo(JSON.stringify(p), participants);
      if (!seal) fail("Couldn't secure that contact for the new chat.");
      put('contactPerDeviceKeys', seal.map);
      put('senderDeviceId', seal.senderDeviceId);
      return { F, S };
    }

    case 'poll': {
      const p = m._payload;
      if (!p?.question) fail("Open that poll first so it can be re-encrypted for the new chat.");
      const seal = await sealTo(JSON.stringify(p), participants);
      if (!seal) fail("Couldn't secure that poll for the new chat.");
      put('pollPerDeviceKeys', seal.map);
      put('senderDeviceId', seal.senderDeviceId);
      // 🟥 PRIVACY: the vote arrays hold raw UIDs of people in the SOURCE chat. Copying
      //    them would leak who voted to a room that never saw the poll.
      // 🟥 AND totalVotes MUST GO TO ZERO WITH THEM. The app carries the old total over
      //    while emptying the arrays, which every reader on both clients then
      //    contradicts: per-option bars are derived from votes.length, so the card reads
      //    "7 votes" with every bar at 0%, and the first real vote recomputes the total
      //    down to 1. An emptied poll is a fresh poll; this reports it as one.
      const options = (m.options || []).map((o) => ({ id: o.id, votes: [] }));
      F.options = options;
      S.options = JSON.stringify(options);
      F.multipleChoice = !!p.multipleChoice; S.multipleChoice = String(!!p.multipleChoice);
      F.privateVoting = !!m.privateVoting; S.privateVoting = String(!!m.privateVoting);
      F.totalVotes = 0; S.totalVotes = '0';
      F.votedUsers = []; S.votedUsers = '[]';
      put('originalPollId', m.originalPollId || m.id);
      put('originalChatId', m.originalChatId || m.chatId || null);
      return { F, S };
    }

    case 'flight': {
      // plaintext on both clients — nothing to unseal, nothing to re-seal
      if (m.flightData) put('flightData', m.flightData);
      if (m.flightNumber) put('flightNumber', m.flightNumber);
      return { F, S };
    }

    default:
      fail("That message type can't be forwarded yet.");
      return { F, S };
  }
}

/**
 * Forward ONE message to ONE chat.
 *
 * @param {object} p.message   the rendered (decrypted) source message
 * @param {object} p.chat      destination — { id, participants, isGroup }
 * @param {string} p.myUid
 * @param {string} p.myName
 * @param {string} p.originalSenderName  resolved by the caller, which is the only
 *                                       place that knows the source chat's names
 * @param {string} [p.sourceChatId]
 */
export async function forwardMessage({
  message, chat, myUid, myName, originalSenderName, sourceChatId,
}) {
  if (!canForward(message)) throw new Error("That message can't be forwarded.");
  if (!chat?.id) throw new Error('No destination.');
  /* 🧩 an album forwards what is still part of it for me — or the one item asked for */
  if (message && message.type === 'images' && Array.isArray(message.images)) {
    const ix = Array.isArray(message._forwardIndices) ? message._forwardIndices : albumVisibleIndices(message, myUid);
    if (ix.length !== message.images.length || Array.isArray(message._forwardIndices)) message = narrowAlbum(message, ix);
  }

  const participants = sealMembers(chat);   // 🔐 active members only
  const isGroup = !!chat.isGroup;
  const type = message.type === 'sticker' || message.type === 'gif' ? 'image' : (message.type || 'text');
  const clientTempId = `temp-forward-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

  await assertReachable(participants, myUid);
  const { F, S } = await bodyFor(message, participants);

  // ── the text seal ────────────────────────────────────────────────────────
  // For a text message this is the message. For everything else it's the phone's
  // localized preview ("📷 Photo"), sealed so the recipient's notification and chat
  // list can read it — which is exactly why an empty one must NOT be sealed.
  const raw = type === 'text'
    ? (message._plain || message.text || '')
    : forwardPreviewOf(message);

  let textFields = {};
  let socketMessage = raw;
  if (raw) {
    const sealed = await sealForUsers(raw, participants);
    if (!sealed || !Object.keys(sealed.perDeviceKeys || {}).length) {
      // ⛔ FAIL CLOSED, like the app: never fall back to shipping plaintext
      throw new Error("Couldn't secure that message for the new chat — nothing was sent.");
    }
    textFields = {
      text: '\u{1F512} [Encrypted Message]',
      perDeviceKeys: sealed.perDeviceKeys,
      senderDeviceId: sealed.senderDeviceId,
    };
    // the socket carries ciphertext for text, but the readable preview for media —
    // the server uses it to compose the push notification
    socketMessage = type === 'text' ? '\u{1F512} [Encrypted Message]' : raw;
  } else {
    // an album seals nothing (see forwardPreviewOf), but the PUSH still needs words —
    // an empty notification body is the one thing worse than a generic one
    textFields = { text: '' };
    socketMessage = type === 'images' ? '\u{1F4F7} Photos' : '';
  }

  const msgRef = doc(collection(db, 'chats', chat.id, 'messages'));
  const messageId = msgRef.id;

  await setMessageDoc(msgRef, {
    type,
    clientTempId,
    senderId: myUid,
    timestamp: serverTimestamp(),
    forwarded: true,
    originalSenderId: message.senderId || null,
    originalSenderName: originalSenderName || 'Unknown User',
    ...(sourceChatId ? { forwardedFrom: sourceChatId } : {}),
    sent: true,
    delivered: false,
    seen: false,
    ...textFields,
    // 🟥 the media seal's senderDeviceId must WIN over the text seal's. Both are this
    //    device today so they agree, but the ordering is the contract the app relies on
    //    (it overwrites in exactly this direction), and a doc may carry only one.
    ...F,
    ...(isGroup ? { senderName: myName || 'Web', isGroup: true } : {}),
  });

  relayNewMessage({
    chatId: chat.id, messageId, fromUserId: myUid, participants, isGroup,
    cloudText: socketMessage, clientMsgId: clientTempId,
    extra: {
      type,
      // 🟥 the app sends this as the STRING 'true' and normalises with
      //    `data.forwarded === 'true' || data.forwarded === true` on the way in.
      //    A real boolean survives socket.io fine, but FCM data payloads are
      //    string-only — so string it is, on both legs.
      forwarded: 'true',
      originalSenderId: message.senderId || null,
      originalSenderName: originalSenderName || 'Unknown User',
      ...(textFields.perDeviceKeys ? {
        perDeviceKeys: textFields.perDeviceKeys,
        senderDeviceId: textFields.senderDeviceId,
      } : {}),
      ...S,
      ...(isGroup ? { senderName: myName || 'Web' } : {}),
    },
  });

  // 🟢 a forwarded poll has to JOIN ITS MASTER. The app registers every copy under
  //    poll_registry/<originalPollId> so a vote cast on any copy fans back out; a copy
  //    that never registers is an island whose votes nobody else ever sees.
  if (type === 'poll' && F.originalPollId) {
    try {
      await setDoc(
        doc(db, 'poll_registry', String(F.originalPollId)),
        { copies: arrayUnion({ chatId: chat.id, messageId }) },
        { merge: true },
      );
    } catch { /* the poll still works locally; only cross-copy sync is lost */ }
  }

  await writeChatDoc({ chat, myUid, myName, messageId, preview: chatDocPreview(message) });
  return messageId;
}

/**
 * Forward one message to several chats. Sequential on purpose: each destination
 * re-seals against its own device directory, and firing them together would stampede
 * the same Firestore reads. Never rejects — it reports per-target outcomes so one
 * failed destination can't silently swallow the rest.
 */
export async function forwardToMany({
  message, chats, myUid, myName, originalSenderName, sourceChatId, onProgress,
}) {
  const results = [];
  for (let i = 0; i < chats.length; i += 1) {
    const chat = chats[i];
    onProgress?.({ index: i, total: chats.length, chat });
    try {
      // eslint-disable-next-line no-await-in-loop
      await forwardMessage({ message, chat, myUid, myName, originalSenderName, sourceChatId });
      results.push({ chat, ok: true });
    } catch (e) {
      results.push({ chat, ok: false, error: e?.message || 'Forward failed' });
    }
  }
  return results;
}

/**
 * Make sure a 1:1 chat exists before forwarding into it.
 *
 * 🟢 writeChatDoc() would create it anyway — it merges — but it writes only the fields a
 *    message preview needs. A chat born that way is missing `isGroup` and `createdAt`,
 *    which App.startChat does write, so the phone would see a subtly different-shaped
 *    conversation than one started normally. One getDoc per NEW destination, and nothing
 *    at all for chats that already exist.
 */
export async function ensureDirectChat(chatId, participants) {
  const ref = doc(db, 'chats', chatId);
  try {
    const snap = await getDoc(ref);
    if (snap.exists()) return;
    await setDoc(ref, {
      participants,
      isGroup: false,
      createdAt: serverTimestamp(),
      lastMessageTime: serverTimestamp(),
    }, { merge: true });
  } catch { /* writeChatDoc will still create a usable chat */ }
}

/** "Forwarded from You" / "Forwarded from Nora" / plain "Forwarded". */
export function forwardedLabel(m, myUid) {
  if (!m?.forwarded) return null;
  if (m.originalSenderId && m.originalSenderId === myUid) return 'Forwarded from You';
  const name = m.originalSenderName;
  // the phone had a bug where some old rows stored the literal "You"; showing that to
  // someone else would credit them with a message they never wrote
  if (name && name !== 'You' && name !== 'Unknown User') return `Forwarded from ${name}`;
  return 'Forwarded';
}
